import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import SignalCellularAltIcon from "@material-ui/icons/SignalCellularAlt";
import WarningIcon from "@material-ui/icons/Warning";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: "auto",
  },
  summaryCard: {
    height: "100%",
  },
  summaryValue: {
    fontSize: "1.9rem",
    fontWeight: 700,
    lineHeight: 1.1,
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  tableWrapper: {
    overflowX: "auto",
  },
  helperText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  warningBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,152,0,0.12)" : "#fff8e1",
    marginBottom: theme.spacing(2),
  },
}));

const buildSummaryCards = overview => [
  {
    key: "total",
    label: i18n.t("officialHealthDashboard.cards.total"),
    value: overview?.total || 0,
  },
  {
    key: "healthy",
    label: i18n.t("officialHealthDashboard.cards.healthy"),
    value: overview?.healthy || 0,
  },
  {
    key: "limited",
    label: i18n.t("officialHealthDashboard.cards.limited"),
    value: overview?.limited || 0,
  },
  {
    key: "blocked",
    label: i18n.t("officialHealthDashboard.cards.blocked"),
    value: overview?.blocked || 0,
  },
  {
    key: "missingConfig",
    label: i18n.t("officialHealthDashboard.cards.missingConfig"),
    value: overview?.missingConfig || 0,
  },
];

const renderBooleanChip = value => {
  const positive = value === true || value === "true";

  return (
    <Chip
      size="small"
      label={positive ? i18n.t("officialHealthDashboard.status.healthy") : i18n.t("officialHealthDashboard.status.unhealthy")}
      style={{
        backgroundColor: positive ? "#e8f5e9" : "#ffebee",
        color: positive ? "#2e7d32" : "#c62828",
        fontWeight: 600,
      }}
    />
  );
};

const renderStatusChip = value => {
  if (!value) {
    return <Chip size="small" label="-" />;
  }

  const colorMap = {
    AVAILABLE: { background: "#e8f5e9", color: "#2e7d32" },
    LIMITED: { background: "#fff8e1", color: "#ef6c00" },
    BLOCKED: { background: "#ffebee", color: "#c62828" },
    CONNECTED: { background: "#e3f2fd", color: "#1565c0" },
    DISCONNECTED: { background: "#fafafa", color: "#616161" },
    PENDING: { background: "#fff8e1", color: "#ef6c00" },
    FLAGGED: { background: "#fce4ec", color: "#ad1457" },
    RESTRICTED: { background: "#ffebee", color: "#c62828" },
    GREEN: { background: "#e8f5e9", color: "#2e7d32" },
    YELLOW: { background: "#fff8e1", color: "#ef6c00" },
    RED: { background: "#ffebee", color: "#c62828" },
  };

  const colors = colorMap[value] || { background: "#eceff1", color: "#455a64" };

  return (
    <Chip
      size="small"
      label={value}
      style={{
        backgroundColor: colors.background,
        color: colors.color,
        fontWeight: 600,
      }}
    />
  );
};

const renderErrorChip = connection => {
  const errorMessage =
    connection.healthCheck?.error?.message ||
    connection.wabaInfo?.error?.message ||
    connection.qualityRating?.error?.message;

  if (!errorMessage) {
    return "-";
  }

  const label = errorMessage.toLowerCase().includes("partner app token")
    ? i18n.t("officialHealthDashboard.status.partnerTokenRequired")
    : i18n.t("officialHealthDashboard.status.error");

  return (
    <Tooltip title={errorMessage}>
      <Chip size="small" label={label} />
    </Tooltip>
  );
};

const OfficialHealthDashboard = () => {
  const classes = useStyles();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [includeRatings, setIncludeRatings] = useState(false);

  const loadDashboard = async forceRefresh => {
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const { data } = await api.get("/whatsapp/official/health", {
        params: {
          refresh: forceRefresh,
          includeRatings,
        },
      });

      setDashboard(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard(false);
  }, [includeRatings]);

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <SignalCellularAltIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          {i18n.t("officialHealthDashboard.title")}
        </Title>
        <div className={classes.headerActions}>
          <FormControlLabel
            control={
              <Switch
                checked={includeRatings}
                onChange={event => setIncludeRatings(event.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("officialHealthDashboard.includeRatings")}
          />
          <Tooltip title={i18n.t("officialHealthDashboard.refresh")}>
            <span>
              <IconButton onClick={() => loadDashboard(true)} disabled={refreshing || loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Typography className={classes.helperText}>
          {i18n.t("officialHealthDashboard.description")}
        </Typography>

        <div className={classes.warningBox}>
          <WarningIcon color="secondary" />
          <Typography variant="body2">
            {i18n.t("officialHealthDashboard.ratingWarning")}
          </Typography>
        </div>

        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {buildSummaryCards(dashboard?.overview).map(card => (
                <Grid item xs={12} sm={6} md={2} key={card.key}>
                  <Card variant="outlined" className={classes.summaryCard}>
                    <CardContent>
                      <Typography className={classes.summaryValue}>{card.value}</Typography>
                      <Typography className={classes.summaryLabel}>{card.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box marginTop={3} className={classes.tableWrapper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("officialHealthDashboard.table.connection")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.provider")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.health")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.canSend")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.quality")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.limit")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.docker")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.rating")}</TableCell>
                    <TableCell>{i18n.t("officialHealthDashboard.table.errors")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboard?.connections?.map(connection => {
                    const wabaInfo = connection.wabaInfo?.data?.wabaInfo;
                    const rating = connection.qualityRating?.data;
                    const errors = wabaInfo?.errors || [];

                    return (
                      <TableRow key={connection.id}>
                        <TableCell>
                          <Typography variant="body2"><strong>{connection.name}</strong></Typography>
                          <Typography variant="caption" color="textSecondary">
                            {connection.phoneNumber || connection.phoneNumberId || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{connection.provider}</Typography>
                          {connection.config?.tokenSource === "legacy_api_token_fallback" && (
                            <Typography variant="caption" color="textSecondary">
                              {i18n.t("officialHealthDashboard.usingLegacyToken")}
                            </Typography>
                          )}
                          {!connection.config?.hasGupshupConfig && (
                            <Typography variant="caption" color="error">
                              {i18n.t("officialHealthDashboard.missingConfig")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {connection.healthCheck?.data ? renderBooleanChip(connection.healthCheck.data.healthy) : (
                            <Tooltip title={connection.healthCheck?.error?.message || "-"}>
                              <Chip size="small" label={i18n.t("officialHealthDashboard.status.unavailable")} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{renderStatusChip(wabaInfo?.canSendMessage)}</TableCell>
                        <TableCell>{renderStatusChip(wabaInfo?.phoneQuality)}</TableCell>
                        <TableCell>{wabaInfo?.messagingLimit || "-"}</TableCell>
                        <TableCell>{renderStatusChip(wabaInfo?.dockerStatus)}</TableCell>
                        <TableCell>
                          {rating?.phoneQuality ? renderStatusChip(rating.phoneQuality) : "-"}
                        </TableCell>
                        <TableCell>
                          {errors.length > 0 ? (
                            <Tooltip title={errors.map(error => `${error.error_code}: ${error.error_description}`).join(" | ")}>
                              <Chip size="small" color="secondary" label={errors.length} />
                            </Tooltip>
                          ) : (
                            renderErrorChip(connection)
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <Box marginTop={2} display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => loadDashboard(true)}
                disabled={refreshing}
              >
                {refreshing ? i18n.t("officialHealthDashboard.refreshing") : i18n.t("officialHealthDashboard.refresh")}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default OfficialHealthDashboard;